import React, { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ArrowUpRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Brand from "./Brand";
import { calendlyUrl, calendlyLinkProps } from "../../lib/calendly";

const navLinks = [
  { to: "/", label: "Home" },
  {
    label: "Services",
    dropdown: [
      { to: "/services", label: "Overview" },
      { to: "/services/rpo", label: "RPO — Recruitment" },
      { to: "/services/bpo", label: "BPO — Operations" },
      { to: "/services/kpo", label: "KPO — Data & Knowledge" },
    ],
  },
  { to: "/ai", label: "AI at A4 Assist" },
  { to: "/industries", label: "Industries" },
  { to: "/case-studies", label: "Case Studies" },
  { to: "/about", label: "About" },
  { to: "/careers", label: "Careers" },
];

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#050B14]/85 backdrop-blur-xl border-b border-white/[0.06]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 h-16 sm:h-20 flex items-center justify-between">
        {/* Wordmark */}
        <Link to="/" data-testid="brand-wordmark" className="flex items-center group">
          <Brand size="sm" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map((item) =>
            item.dropdown ? (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}
              >
                <button
                  data-testid="nav-services-toggle"
                  className="flex items-center gap-1 text-[13.5px] text-slate-300 hover:text-[#00E5FF] transition-colors"
                >
                  {item.label}
                  <ChevronDown size={14} className={`transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {servicesOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 6 }}
                      transition={{ duration: 0.18 }}
                      className="absolute top-full left-1/2 -translate-x-1/2 pt-4"
                    >
                      <div className="w-64 rounded-xl border border-slate-800 bg-[#080F1F]/95 backdrop-blur-xl shadow-2xl p-2">
                        {item.dropdown.map((d) => (
                          <NavLink
                            key={d.to}
                            to={d.to}
                            data-testid={`nav-dropdown-${d.to.replace(/\//g, "-")}`}
                            className={({ isActive }) =>
                              `flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-colors ${
                                isActive
                                  ? "bg-[#00E5FF]/10 text-[#00E5FF]"
                                  : "text-slate-300 hover:bg-white/5 hover:text-white"
                              }`
                            }
                          >
                            {d.label}
                            <ArrowUpRight size={14} className="opacity-50" />
                          </NavLink>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-${item.to.replace(/\//g, "-") || "home"}`}
                className={({ isActive }) =>
                  `text-[13.5px] transition-colors ${
                    isActive ? "text-[#00E5FF]" : "text-slate-300 hover:text-[#00E5FF]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="header-contact-link"
            className="hidden md:inline-block text-[13.5px] text-slate-300 hover:text-white transition-colors"
          >
            Contact
          </Link>
          <a
            href={calendlyUrl()}
            {...calendlyLinkProps}
            data-testid="header-cta-button"
            className="hidden sm:inline-flex items-center gap-2 bg-[#00E5FF] hover:bg-[#00E5FF]/90 text-[#050B14] font-semibold text-[13px] px-4 py-2.5 rounded-md transition-colors shadow-[0_0_25px_-8px_rgba(0,229,255,0.6)]"
          >
            Free Operations Audit
          </a>
          <button
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 text-white"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            data-testid="mobile-menu"
            className="lg:hidden overflow-hidden bg-[#050B14]/98 backdrop-blur-xl border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {navLinks.map((item) =>
                item.dropdown ? (
                  <div key={item.label} className="flex flex-col">
                    <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#00E5FF] py-2 mt-1">
                      {item.label}
                    </div>
                    {item.dropdown.map((d) => (
                      <NavLink
                        key={d.to}
                        to={d.to}
                        className="text-slate-200 hover:text-[#00E5FF] py-2 text-sm pl-3 border-l border-slate-800"
                      >
                        {d.label}
                      </NavLink>
                    ))}
                  </div>
                ) : (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === "/"}
                    className="text-slate-200 hover:text-[#00E5FF] py-2.5 text-sm"
                  >
                    {item.label}
                  </NavLink>
                )
              )}
              <Link to="/contact" className="text-slate-200 hover:text-[#00E5FF] py-2.5 text-sm">
                Contact
              </Link>
              <a
                href={calendlyUrl()}
                {...calendlyLinkProps}
                className="mt-4 bg-[#00E5FF] text-[#050B14] font-semibold px-4 py-3 rounded-md text-center"
              >
                Free Operations Audit
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
